import React, { useState } from 'react';
import Button from 'shared/components/UC/Button';
import styles from './NotifyResultsModal.module.css';

export interface ApplicationToNotify {
  id: string;
  applicationNumber: string;
  projectTitle: string;
  user: string;
}

export interface NotificationStats {
  selected: ApplicationToNotify[];
  not_selected: ApplicationToNotify[];
  selected_notified: ApplicationToNotify[];
  not_selected_notified: ApplicationToNotify[];
}

export interface NotificationResult {
  notifiedAt: string;
  selectedCount: number;
  notSelectedCount: number;
  adminName: string;
}

export interface NotifyResultsModalProps {
  isOpen: boolean;
  stats: NotificationStats;
  onClose: () => void;
  onConfirm: (applicationIds: string[]) => Promise<NotificationResult>;
}

const NotifyResultsModal: React.FC<NotifyResultsModalProps> = ({
  isOpen,
  stats,
  onClose,
  onConfirm,
}) => {
  const [isNotifying, setIsNotifying] = useState(false);
  const [result, setResult] = useState<NotificationResult | null>(null);
  const canNotify = stats.selected.length + stats.not_selected.length > 0;
  const handleConfirm = async () => {
    try {
      setIsNotifying(true);
      // Combine both selected and not_selected application IDs
      const selectedIds = stats.selected.map((app) => app.id);
      const notSelectedIds = stats.not_selected.map((app) => app.id);
      const applicationIds = [...selectedIds, ...notSelectedIds];
      const notificationResult = await onConfirm(applicationIds);
      setResult(notificationResult);
    } catch (error) {
      // Error handling is done in the parent component
      setIsNotifying(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Notificar resultados a postulantes</h2>
          <button className={styles.closeButton} onClick={handleClose}>
            ×
          </button>
        </div>

        <div className={styles.content}>
          {!result ? (
            <>
              <div className={styles.section}>
                <h3>Resumen de notificaciones a enviar</h3>
                <div className={styles.statsGrid}>
                  <div className={styles.statCard}>
                    <div className={styles.statNumber}>
                      {stats.selected.length}
                    </div>
                    <div className={styles.statLabel}>
                      Postulaciones Adjudicadas
                    </div>
                    <div className={styles.statLabel}>
                      {stats.selected_notified?.length > 0
                        ? `(Ya notificadas: ${stats.selected_notified.length})`
                        : ''}
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statNumber}>
                      {stats.not_selected.length}
                    </div>
                    <div className={styles.statLabel}>
                      Postulaciones No adjudicadas
                    </div>
                    <div className={styles.statLabel}>
                      {stats.not_selected_notified?.length > 0
                        ? `(Ya notificadas: ${stats.not_selected_notified.length})`
                        : ''}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.warningSection}>
                <div className={styles.warningIcon}>⚠️</div>
                <div className={styles.warningContent}>
                  <h4>Advertencia</h4>
                  <p>
                    Al presionar "Notificar a postulantes" se enviará un email
                    masivo con el resultado de la postulación a cada
                    Investigador Responsable que postuló.
                  </p>
                  <p>
                    Si un postulante ya fue notificado no se le enviará un nuevo
                    email.
                  </p>
                  <p>
                    El envío de emails puede tardar un tiempo en completarse.
                    Las fechas de notificación se irán actualizando a medida que
                    se envíen los correos.
                  </p>
                  <p>
                    <strong>Total de emails a enviar:</strong>{' '}
                    {stats.selected.length + stats.not_selected.length}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className={styles.successSection}>
              <div className={styles.successIcon}>✓</div>
              <h3>Notificaciones enviadas</h3>
              <p className={styles.successMessage}>
                Las notificaciones serán enviadas a todos los postulantes. Las
                fechas de notificación se irán actualizando en la medida que los
                emails sean enviados.
              </p>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {!result ? (
            <>
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isNotifying}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                disabled={isNotifying || !canNotify}
              >
                {isNotifying
                  ? 'Enviando...'
                  : canNotify
                    ? 'Notificar a postulantes'
                    : 'No hay postulaciones para notificar'}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={handleClose}>
              Cerrar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotifyResultsModal;
